import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Booking } from '../../models/Booking'
import { Estimate } from '../../models/Estimate'
import { Gift } from '../../models/Gift'
import { Invoice } from '../../models/Invoice'
import { Package } from '../../models/Package'
import { Payment } from '../../models/Payment'
import { RecordFactory } from '../../models/RecordFactory'
import { Subscription } from '../../models/Subscription'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { BOOK_BASE } from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import SegmentedControl from '../app/design/SegmentedControl'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { Chip } from '../common/fields/Chip'
import { CurrencyField } from '../common/fields/CurrencyField'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { PaymentView } from './PaymentView'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    if (!urlParams.id) {
        return new RecordNotFoundError('Could not find payment')
    }

    await RecordFactory.getInstance().fetchPayment(urlParams.id)
    const payment = await RecordFactory.getInstance().loadPayment(urlParams.id)
    if (!payment) {
        return new RecordNotFoundError('Could not find payment')
    }

    const sourceTransaction = await (async (): Promise<
        | { type: 'booking'; booking: Booking }
        | { type: 'estimate'; estimate: Estimate }
        | { type: 'package'; aPackage: Package }
        | { type: 'gift'; gift: Gift }
        | { type: 'subscription'; subscription: Subscription }
        | { type: 'invoice'; invoice: Invoice }
        | null
    > => {
        if (payment.booking?.id !== undefined) {
            const booking = await RecordFactory.getInstance().loadBooking(payment.booking.id)
            if (booking === undefined) return null
            return { type: 'booking', booking }
        }
        else if (payment.estimate?.id !== undefined) {
            const estimate = await RecordFactory.getInstance().loadEstimate(payment.estimate.id)
            if (estimate === undefined) return null
            return { type: 'estimate', estimate }
        }
        else if (payment.aPackage?.id !== undefined) {
            const aPackage = await RecordFactory.getInstance().loadPackage(payment.aPackage.id)
            if (aPackage === undefined) return null
            return { type: 'package', aPackage }
        }
        else if (payment.gift?.id !== undefined) {
            const gift = await RecordFactory.getInstance().loadGift(payment.gift.id)
            if (gift === undefined) return null
            return { type: 'gift', gift }
        }
        else if (payment.subscription?.id !== undefined) {
            const subscription = await RecordFactory.getInstance().loadSubscription(
                payment.subscription.id
            )
            if (subscription === undefined) return null
            return { type: 'subscription', subscription }
        }
        else if (payment.invoice?.id !== undefined) {
            const invoice = await RecordFactory.getInstance().loadInvoice(payment.invoice.id)
            if (invoice === undefined) return null
            return { type: 'invoice', invoice }
        }
        else {
            return null
        }
    })()

    return { payment, sourceTransaction }
}

const render = ({
    data,
    base: { owner, logAndDisplayError, me },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { payment, sourceTransaction } = data

    usePageTitle('Payment')

    const navigate = useNavigate()

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
    } | null>(null)

    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
    const [isRefundLoading, setIsRefundLoading] = useState(false)
    const [refundState, setRefundState] = useState<{
        amountType: 'full-refund' | 'full-refund-minus-fees' | 'partial-refund'
        partialRefundAmount: number
        reason: 'cancel' | 'overcharge' | 'courtesy' | 'paid'
        details: string
    }>({
        amountType: 'full-refund',
        partialRefundAmount: 0,
        reason: 'cancel',
        details: '',
    })

    const forVendor = owner.id === payment.vendor?.id
    const forClient = me.id === payment.client?.id

    const contact = forClient
        ? (payment.employee ??
          (!!payment.creator && payment.creator.id !== me.id ? payment.creator : payment.vendor))
        : payment.client
    if (!contact) {
        throw new RecordNotFoundError('Could not find contact')
    }

    const isRefundButtonEnabled =
        refundState.amountType !== 'partial-refund' || refundState.partialRefundAmount > 0

    const fullRefundAmount = (payment.amount ?? 0) - (payment.refundedAmount ?? 0)
    const fullRefundMinusFeesAmount = (payment.payout ?? 0) - (payment.refundedAmount ?? 0)

    const handleRefund = async () => {
        if (!isRefundButtonEnabled) {
            return
        }

        const refundPayment = new Payment({ id: payment.id })
        refundPayment.refundReason = refundState.reason
        refundPayment.refundDetails = refundState.details

        const refundAmount =
            refundState.amountType === 'full-refund'
                ? fullRefundAmount
                : refundState.amountType === 'full-refund-minus-fees'
                  ? fullRefundMinusFeesAmount
                  : refundState.partialRefundAmount

        setIsRefundLoading(true)
        const result = await RecordFactory.getInstance().submitRefund(refundPayment, refundAmount)
        setIsRefundLoading(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        window.location.reload()
    }

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        buttons.push({
            title: 'Receipt',
            onClick: () =>
                setConfirmationModalData({
                    title: 'Send receipt',
                    confirmationButtonText: 'Send receipt',
                    body: 'Would you like to send the client a receipt?',
                    onConfirm: async () => {
                        setConfirmationModalData(null)
                        await RecordFactory.getInstance().sendReceipt(payment)
                        if (payment.client?.id) {
                            navigate(`/messages/${payment.client?.id}`)
                        }
                    },
                }),
        })

        buttons.push({
            title: 'Print',
            onClick: () => window.open(`${BOOK_BASE}/view/${payment.id}/download`, '_blank'),
        })

        if (forVendor && !payment.cancelledDate && !payment.refundReason) {
            buttons.push({
                title: 'Refund',
                onClick: () => setIsRefundModalOpen(true),
            })
        }

        return buttons
    })()

    const backHistoryEntry = findLastHistoryEntry({ excluding: /^\/charge.*/ })

    return (
        <BaseRecordView
            title="Payment"
            tag={payment.statusTag ?? undefined}
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : { title: 'Home', onClick: () => navigate('/') }
            }
            headerButtons={headerButtons}
        >
            <PaymentView
                payment={payment}
                contact={contact}
                sourceTransaction={sourceTransaction}
                forVendor={forVendor}
                owner={owner}
                isSignedInUserPayer={me.id === payment.client?.id}
            />

            {confirmationModalData !== null && (
                <Modal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    size="small"
                    title={confirmationModalData.title}
                    showDividers={false}
                    footer={
                        <div className="flex w-full items-center justify-end gap-x-4">
                            <Button
                                intent="primary"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    confirmationModalData.onConfirm()
                                }}
                            >
                                {confirmationModalData.confirmationButtonText}
                            </Button>
                        </div>
                    }
                >
                    {confirmationModalData.body}
                </Modal>
            )}

            {isRefundModalOpen && (
                <Modal
                    isOpen
                    onClose={() => setIsRefundModalOpen(false)}
                    title="Refund"
                    footer={
                        <Button
                            intent="secondary"
                            onClick={handleRefund}
                            disabled={!isRefundButtonEnabled || isRefundLoading}
                            isLoading={isRefundLoading}
                        >
                            Send refund
                        </Button>
                    }
                >
                    <div className="flex flex-col items-stretch justify-start gap-y-8">
                        <WorkflowPage.IconSection
                            title="Refund amount"
                            description="Select a full refund or enter a partial refund amount"
                            icon="dollar.large"
                        >
                            <div className="flex flex-col items-stretch justify-start gap-y-4">
                                <Select
                                    options={[
                                        {
                                            id: 'full-refund',
                                            title: `Full refund (with fees) · ${currencyFormat(fullRefundAmount)}`,
                                        },
                                        {
                                            id: 'full-refund-minus-fees',
                                            title: `Full refund (minus fees) · ${currencyFormat(fullRefundMinusFeesAmount)}`,
                                        },
                                        { id: 'partial-refund', title: 'Partial refund' },
                                    ]}
                                    selectedOptionID={refundState.amountType}
                                    onSelectOption={(option) => {
                                        setRefundState({
                                            ...refundState,
                                            amountType: option.id as typeof refundState.amountType,
                                        })
                                    }}
                                    size="medium"
                                />

                                {refundState.amountType === 'partial-refund' && (
                                    <CurrencyField
                                        value={refundState.partialRefundAmount}
                                        onChange={(newAmount) =>
                                            setRefundState({
                                                ...refundState,
                                                partialRefundAmount: newAmount ?? 0,
                                            })
                                        }
                                        size="medium"
                                    />
                                )}
                            </div>
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Reason for refund"
                            description="Select a reason for refunding the payment"
                            icon="dollar2.large"
                        >
                            <div className="flex flex-wrap justify-start gap-x-4 gap-y-2">
                                <Chip
                                    title="Services not rendered"
                                    size="small"
                                    isSelected={refundState.reason === 'cancel'}
                                    onClick={() =>
                                        setRefundState({
                                            ...refundState,
                                            reason: 'cancel',
                                        })
                                    }
                                />
                                <Chip
                                    title="Overcharged"
                                    size="small"
                                    isSelected={refundState.reason === 'overcharge'}
                                    onClick={() =>
                                        setRefundState({ ...refundState, reason: 'overcharge' })
                                    }
                                />
                                <Chip
                                    title="Courtesy refund"
                                    size="small"
                                    isSelected={refundState.reason === 'courtesy'}
                                    onClick={() =>
                                        setRefundState({
                                            ...refundState,
                                            reason: 'courtesy',
                                        })
                                    }
                                />
                                <Chip
                                    title="Client already paid"
                                    size="small"
                                    isSelected={refundState.reason === 'paid'}
                                    onClick={() =>
                                        setRefundState({
                                            ...refundState,
                                            reason: 'paid',
                                        })
                                    }
                                />
                            </div>
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="More details"
                            description="Add more details about the refund and this will be added to the receipt."
                            secondaryText={`${refundState.details.length} / 999`}
                            icon="note.large"
                        >
                            <TextArea
                                value={refundState.details}
                                onChange={(e) =>
                                    setRefundState({
                                        ...refundState,
                                        details: e.target.value,
                                    })
                                }
                                maxLength={999}
                                className="max-h-[170px] min-h-[74px]"
                                size="medium"
                                onClear={() =>
                                    setRefundState({
                                        ...refundState,
                                        details: '',
                                    })
                                }
                                placeholder="Enter more details"
                                isAutoResizing
                            />
                        </WorkflowPage.IconSection>
                    </div>
                </Modal>
            )}
        </BaseRecordView>
    )
}

export const PaymentPage = usePSComponent(render, viewWillAppear)
